import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useT, usePageTitle, Trans } from "@/i18n/LanguageContext";
import ProductCard from "@/components/ProductCard";

const PROMO_SLUG = "promo";

export default function Promo() {
    const t = useT();
    usePageTitle(t("meta.title.promo"));
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState("");
    const [style, setStyle] = useState("");

    useEffect(() => {
        let cancelled = false;
        api.get("/categories")
            .then((r) => {
                // The backend ignores an unknown category slug (it would return the
                // whole catalog), so only fetch products if the promo category exists.
                const promo = (r.data || []).find((c) => c.slug === PROMO_SLUG);
                if (!promo) return { data: { items: [] } };
                return api.get("/products", {
                    params: { visible: true, category: PROMO_SLUG, page_size: 100 },
                });
            })
            .then((r) => {
                if (!cancelled) setProducts(r.data.items ?? []);
            })
            .catch(() => {
                if (!cancelled) setProducts([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Other categories the promo pieces belong to (besides PROMO itself)
    const categories = useMemo(() => {
        const s = new Set();
        products.forEach((p) =>
            (p.category_names || []).forEach((n) => {
                if (n.toLowerCase() !== PROMO_SLUG) s.add(n);
            })
        );
        return Array.from(s).sort((a, b) => a.localeCompare(b));
    }, [products]);

    const filtered = useMemo(
        () =>
            products.filter(
                (p) =>
                    (!category || (p.category_names || []).includes(category)) &&
                    (!style || p.style === style)
            ),
        [products, category, style]
    );

    return (
        <>
            <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 md:pt-28 pb-12" data-testid="promo-header">
                <p className="overline reveal">{t("promo.overline")}</p>
                <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[0.95] reveal reveal-delay-1">
                    <Trans tKey="promo.title" />
                </h1>
                <p className="text-melilux-cream/65 max-w-xl mt-8 leading-relaxed reveal reveal-delay-2">
                    {t("promo.lead")}
                </p>
            </section>

            {/* Category filter */}
            {categories.length > 0 && (
                <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10 flex flex-wrap items-center gap-2 border-b border-white/10" data-testid="promo-category-filter">
                    <button
                        onClick={() => setCategory("")}
                        data-testid="promo-cat-all"
                        className={`text-[0.72rem] uppercase tracking-[0.28em] px-4 py-2 border ${
                            !category ? "border-melilux-gold text-melilux-gold" : "border-transparent text-melilux-cream/65 hover:text-melilux-cream"
                        }`}
                    >
                        {t("cat.all")}
                    </button>
                    {categories.map((c) => (
                        <button
                            key={c}
                            onClick={() => setCategory(c)}
                            data-testid={`promo-cat-${c}`}
                            className={`text-[0.72rem] uppercase tracking-[0.28em] px-4 py-2 border transition-colors ${
                                category === c
                                    ? "border-melilux-gold text-melilux-gold"
                                    : "border-transparent text-melilux-cream/65 hover:text-melilux-cream"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </section>
            )}

            {/* Style filter */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center gap-6 text-[0.7rem] uppercase tracking-[0.25em]">
                <div className="flex flex-wrap items-center gap-3" data-testid="promo-style-filter">
                    <span className="text-melilux-cream/45">{t("cat.style")}</span>
                    {["", "Classic", "Modern"].map((s) => (
                        <button
                            key={s || "all"}
                            onClick={() => setStyle(s)}
                            data-testid={`promo-style-${s || "all"}`}
                            className={`px-3 py-1.5 border transition-colors ${
                                style === s
                                    ? "border-melilux-gold text-melilux-gold"
                                    : "border-white/15 text-melilux-cream/65 hover:border-melilux-cream/40 hover:text-melilux-cream"
                            }`}
                        >
                            {s ? t(`style.${s.toLowerCase()}`) : t("cat.all")}
                        </button>
                    ))}
                </div>
                {(category || style) && (
                    <button
                        data-testid="promo-clear-filters"
                        className="text-melilux-gold link-underline"
                        onClick={() => {
                            setCategory("");
                            setStyle("");
                        }}
                    >
                        {t("cat.clear")}
                    </button>
                )}
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-12 mt-6" data-testid="promo-grid">
                {loading ? (
                    <div className="py-32 text-center text-melilux-cream/50 text-[0.7rem] uppercase tracking-[0.3em]">
                        {t("cat.loading")}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="py-32 text-center text-melilux-cream/50" data-testid="promo-empty-state">
                        <p className="font-serif text-3xl">
                            {products.length === 0 ? t("promo.empty.title") : t("cat.empty.title")}
                        </p>
                        <p className="text-sm mt-3">
                            {products.length === 0 ? t("promo.empty.lead") : t("cat.empty.lead")}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-10">
                        {filtered.map((p) => (
                            <ProductCard key={p.id} p={p} />
                        ))}
                    </div>
                )}
            </section>
        </>
    );
}
